import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnalyticsFilters } from '../../../../contexts/AnalyticsFiltersContext';
import { getMockUseAnalytics } from '../../../../mocks/mockTutorUsageData';
import analyticsService from '../../../../services/analyticsService';
import type { Conversation, ConversationMode, DailyInteractionPoint, GetUseAnalyticsResult } from '../../../../types/tutorUsage';
import getDateRange from '../../../../utils/getDateRange';

type TranslateFn = ReturnType<typeof useTranslation>['t'];

type UseTutorUsageSectionProps = {
  allCourseIds: string[];
  classValue: string;
  groupCourseMap: Record<string, string[]>;
  timePeriodValue: string;
};

type StatCardConfig = {
  hasInfo?: boolean;
  iconType: string;
  iconVariant: 'purple' | 'blue' | 'orange' | 'green';
  infoTooltip?: string;
  key: string;
  label: string;
  subtitle: string;
  trend: number | null;
  trendText: string;
  value: string;
};

type ModeFilterValue = 'ALL' | ConversationMode;

type ModeOption = {
  label: string;
  value: ModeFilterValue;
};

type UseTutorUsageSectionReturn = {
  activityTitle: string;
  dailyInteractions: DailyInteractionPoint[];
  data: GetUseAnalyticsResult | null;
  error: string | null;
  isMockMode: boolean;
  loading: boolean;
  modeOptions: ModeOption[];
  onModeChange: (option: { label: string; value: string } | null) => void;
  selectedMode: ModeOption;
  statCards: StatCardConfig[];
  t: TranslateFn;
};

const EMPTY_VALUE = '—';
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function computeTrend(current: number, previous: number | null | undefined): number | null {
  if (previous === null || previous === undefined) return null;
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
}

function getTrendTextKey(timePeriodValue: string): string {
  switch (timePeriodValue) {
    case '30d':
      return 'chatbot:tutor_usage_trend_previous_month';
    case '3m':
      return 'chatbot:tutor_usage_trend_previous_3_months';
    case '7d':
    default:
      return 'chatbot:tutor_usage_trend_previous_week';
  }
}

function getActivityTitleKey(timePeriodValue: string): string {
  switch (timePeriodValue) {
    case '30d':
      return 'chatbot:tutor_usage_activity_title_month';
    case '3m':
      return 'chatbot:tutor_usage_activity_title_3_months';
    case '7d':
    default:
      return 'chatbot:tutor_usage_activity_title_week';
  }
}

function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function uniqueUserCount(period: Conversation[]): number {
  const set = new Set<string>();
  period.forEach((conv) => set.add(conv.userId));
  return set.size;
}

function totalMessages(period: Conversation[]): number {
  return period.reduce((acc, conv) => acc + conv.userMessages.length, 0);
}

function messagesByMode(period: Conversation[], mode: ConversationMode): number {
  return period
    .filter((conv) => conv.mode === mode)
    .reduce((acc, conv) => acc + conv.userMessages.length, 0);
}

function toLocalDayKey(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function capitalize(value: string): string {
  return value.length > 0 ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

function buildDayFormatter(language: string): (date: Date) => string {
  try {
    const formatter = new Intl.DateTimeFormat(language, { weekday: 'long' });
    return (date) => capitalize(formatter.format(date));
  } catch {
    const fallback = new Intl.DateTimeFormat('en', { weekday: 'long' });
    return (date) => fallback.format(date);
  }
}

function buildDailyInteractions(
  conversations: Conversation[],
  startDate: string | undefined,
  endDate: string | undefined,
  language: string,
): DailyInteractionPoint[] {
  if (!startDate || !endDate) return [];

  const counts = new Map<string, number>();
  conversations.forEach((conv) => {
    conv.userMessages.forEach((msg) => {
      const key = toLocalDayKey(msg.createdAt);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
  });

  const formatDayName = buildDayFormatter(language);
  const start = new Date(startDate);
  const end = new Date(endDate);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  const points: DailyInteractionPoint[] = [];
  for (let d = start.getTime(); d <= end.getTime(); d += MS_PER_DAY) {
    const date = new Date(d);
    const key = toLocalDayKey(date.toISOString());
    points.push({
      date: key,
      interactions: counts.get(key) ?? 0,
      name: `${formatDayName(date)} ${date.getDate()}`,
    });
  }

  return points;
}

function buildActiveStudentsCard(
  conversationsCurr: Conversation[] | null,
  conversationsPrev: Conversation[] | null,
  totalUsers: number,
  t: TranslateFn,
  trendText: string,
): StatCardConfig {
  const hasData = conversationsCurr !== null;
  const activeCurrent = conversationsCurr ? uniqueUserCount(conversationsCurr) : 0;
  const activePrevious = conversationsPrev ? uniqueUserCount(conversationsPrev) : null;
  const percentage = totalUsers > 0 ? Math.round((activeCurrent / totalUsers) * 100) : 0;
  return {
    iconType: 'students',
    iconVariant: 'purple',
    key: 'active-students',
    label: t('chatbot:tutor_usage_active_students'),
    subtitle: t('chatbot:tutor_usage_active_students_subtitle', { percentage: hasData ? percentage : 0 }),
    trend: hasData ? computeTrend(activeCurrent, activePrevious) : null,
    trendText,
    value: hasData ? `${activeCurrent}/${totalUsers}` : EMPTY_VALUE,
  };
}

function buildTotalInteractionsCard(
  conversationsCurr: Conversation[] | null,
  conversationsPrev: Conversation[] | null,
  activeCurrent: number,
  t: TranslateFn,
  trendText: string,
): StatCardConfig {
  const hasData = conversationsCurr !== null;
  const currTotal = conversationsCurr ? totalMessages(conversationsCurr) : 0;
  const prevTotal = conversationsPrev ? totalMessages(conversationsPrev) : 0;
  const avgPerStudent = hasData && activeCurrent > 0 ? roundTo(currTotal / activeCurrent, 1) : 0;
  return {
    iconType: 'comment',
    iconVariant: 'blue',
    key: 'total-interactions',
    label: t('chatbot:tutor_usage_total_interactions'),
    subtitle: t('chatbot:tutor_usage_total_interactions_subtitle', { avg: avgPerStudent }),
    trend: hasData ? computeTrend(currTotal, prevTotal) : null,
    trendText,
    value: hasData ? String(currTotal) : EMPTY_VALUE,
  };
}

function buildStudyModeCard(
  conversationsCurr: Conversation[] | null,
  conversationsPrev: Conversation[] | null,
  t: TranslateFn,
  trendText: string,
): StatCardConfig {
  const hasData = conversationsCurr !== null;
  const currStudy = conversationsCurr ? messagesByMode(conversationsCurr, 'STUDY_MODE') : 0;
  const currPractice = conversationsCurr ? messagesByMode(conversationsCurr, 'PRACTICE_MODE') : 0;
  const prevStudy = conversationsPrev ? messagesByMode(conversationsPrev, 'STUDY_MODE') : 0;
  const prevPractice = conversationsPrev ? messagesByMode(conversationsPrev, 'PRACTICE_MODE') : 0;
  const currSum = currStudy + currPractice;
  const prevSum = prevStudy + prevPractice;
  const currStudyRatio = currSum > 0 ? currStudy / currSum : 0;
  const prevStudyRatio = prevSum > 0 ? prevStudy / prevSum : 0;
  const studyPercentage = Math.round(currStudyRatio * 100);
  const practicePercentage = currSum > 0 ? Math.round((currPractice / currSum) * 100) : 0;
  return {
    hasInfo: true,
    iconType: 'book-open',
    iconVariant: 'orange',
    infoTooltip: t('chatbot:tutor_usage_study_mode_tooltip'),
    key: 'study-mode',
    label: t('chatbot:tutor_usage_study_mode'),
    subtitle: t('chatbot:tutor_usage_practice_mode_subtitle', { percentage: practicePercentage }),
    trend: hasData ? computeTrend(Math.round(currStudyRatio * 10000), Math.round(prevStudyRatio * 10000)) : null,
    trendText,
    value: hasData ? `${studyPercentage}%` : EMPTY_VALUE,
  };
}

function useTutorUsageSection({ allCourseIds, classValue, groupCourseMap, timePeriodValue }: UseTutorUsageSectionProps): UseTutorUsageSectionReturn {
  const { isMockMode } = useAnalyticsFilters();
  const { i18n, t } = useTranslation(['chatbot']);

  const [data, setData] = useState<GetUseAnalyticsResult | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedModeValue, setSelectedModeValue] = useState<ModeFilterValue>('ALL');

  const modeOptions = useMemo<ModeOption[]>(
    () => [
      { label: t('chatbot:tutor_usage_all_modes'), value: 'ALL' },
      { label: t('chatbot:tutor_usage_mode_study'), value: 'STUDY_MODE' },
      { label: t('chatbot:tutor_usage_mode_practice'), value: 'PRACTICE_MODE' },
      { label: t('chatbot:tutor_usage_mode_revision'), value: 'REVISION_MODE' },
    ],
    [t],
  );

  const selectedMode = useMemo<ModeOption>(
    () => modeOptions.find((opt) => opt.value === selectedModeValue) ?? modeOptions[0],
    [modeOptions, selectedModeValue],
  );

  const onModeChange = useCallback((option: { label: string; value: string } | null): void => {
    if (!option) return;
    setSelectedModeValue(option.value as ModeFilterValue);
  }, []);

  const currentCourseIds = useMemo(() => {
    return classValue === 'all' ? allCourseIds : (groupCourseMap[classValue] ?? []);
  }, [classValue, allCourseIds, groupCourseMap]);

  const { startDate: currentStartDate, endDate: currentEndDate } = useMemo(() => {
    return getDateRange(timePeriodValue);
  }, [timePeriodValue]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result: GetUseAnalyticsResult = await analyticsService.getUseAnalytics({
        courseIds: currentCourseIds,
        startDate: currentStartDate,
        endDate: currentEndDate,
      });

      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [currentCourseIds, currentStartDate, currentEndDate]);

  useEffect(() => {
    if (isMockMode) {
      setData(getMockUseAnalytics());
      setLoading(false);
      setError(null);
      return;
    }

    if (currentCourseIds.length > 0) {
      fetchData();
    } else {
      setLoading(false);
      setData(null);
    }
  }, [fetchData, isMockMode, currentCourseIds.length]);

  const statCards = useMemo<StatCardConfig[]>(() => {
    const trendText = t(getTrendTextKey(timePeriodValue));
    const conversationsCurr = data?.conversations?.currentPeriod ?? null;
    const conversationsPrev = data?.conversations?.previousPeriod ?? null;
    const totalUsers = data?.totalUsers ?? 0;
    const activeCurrent = conversationsCurr ? uniqueUserCount(conversationsCurr) : 0;

    return [
      buildActiveStudentsCard(conversationsCurr, conversationsPrev, totalUsers, t, trendText),
      buildTotalInteractionsCard(conversationsCurr, conversationsPrev, activeCurrent, t, trendText),
      buildStudyModeCard(conversationsCurr, conversationsPrev, t, trendText),
    ];
  }, [data, t, timePeriodValue]);

  const dailyInteractions = useMemo<DailyInteractionPoint[]>(() => {
    const conversationsCurr = data?.conversations?.currentPeriod ?? [];
    const filtered = selectedModeValue === 'ALL' ? conversationsCurr : conversationsCurr.filter((c) => c.mode === selectedModeValue);
    return buildDailyInteractions(filtered, data?.startDate ?? currentStartDate, data?.endDate ?? currentEndDate, i18n.language);
  }, [data, currentStartDate, currentEndDate, i18n.language, selectedModeValue]);

  const activityTitle = t(getActivityTitleKey(timePeriodValue));

  return { activityTitle, dailyInteractions, data, error, isMockMode, loading, modeOptions, onModeChange, selectedMode, statCards, t };
}

export default useTutorUsageSection;
